
export class BTTrackerBase {
  /**
   * Constructs a new instance of the BTTrackerBase
   * @param {MessageHub} messageHub
   */
  constructor({messageHub}) {
    if (!messageHub) {
      throw Error('Missing required messageHub constructor parameter.');
    }
    this.enabled = true;
    this.debug = false;
    this.events = null;
    this.actions = null;
    this.pageType = '';
    this._page = window.location.pathname;
    this.messageHub = messageHub;
    this._isRestrictedDataProcessingEnabled = false;
    this._initialized = false;

    this._delayedViews = [];
    this._delayedEvents = [];
    this._delayedActions = [];
    this._delayedPurchases = [];
  }

  configure({debug = false} = {}) {
    this.debug = debug;
  }

  canInitialize() {
    if (this.restrictsEventsWithoutConsent && this.isRestrictedDataProcessingEnabled) {
      this.log(
        'This tracker does not support firing events while in restricted data processing mode.',
      );
      return false;
    }
    return true;
  }

  // eslint-disable-next-line no-unused-vars
  init(options, ...rest) {
    if (!this.canInitialize()) {
      return false;
    }

    this._initialized = true;

    this._delayedViews.forEach(v => v());
    this._delayedEvents.forEach(e => e());
    this._delayedActions.forEach(a => a());
    this._delayedPurchases.forEach(p => p());

    return true;
  }

  event(type, params = {}, ...rest) {
    if (!this.initialized) {
      this._delayedEvents.push(() => this.event(type, params, ...rest));
      this.logDebug('Not initialized, delaying EVENT.', {type, params, ...rest});
      return false;
    }
    return true;
  }

  // eslint-disable-next-line no-empty-function, no-unused-vars
  purchase(order, items, options, ...rest) {
    if (!this.initialized && !options.force) {
      this._delayedPurchases.push(() => this.purchase(order, items, options, ...rest));
      this.logDebug('Not initialized, delaying PURCHASE.', {order, items, options, ...rest});
      return false;
    }
    if (!this.initialized) {
      this.init({force: true});
    }
    return true;
  }

  // eslint-disable-next-line no-empty-function
  view(...params) {
    if (!this.initialized) {
      this._delayedViews.push(() => this.view(...params));
      this.logDebug('Not initialized, delaying VIEW.', {...params});
      return false;
    }
    return true;
  }

  /**
   * Action is for user initiated events. Such as a button click, reveal, leads and more
   *
   * positional parameters: type, action, category, data
   */
  action(...params) {
    if (!this.initialized) {
      this._delayedActions.push(() => this.action(...params));
      this.logDebug('Not initialized, delaying ACTION.', {...params});
      return false;
    }
    return true;
  }

  // eslint-disable-next-line no-empty-function, no-unused-vars
  pageDidSet(val) { }

  getName() {
    return 'Unnamed-Tracker';
  }

  get initialized() {
    return this._initialized;
  }

  get order() {
    return 1;
  }

  get page() {
    return this._page;
  }

  /**
   * Indicates if the tracker waits for a consent response
   * before initializing and firing its events without Data Processing Options that
   * restricts usage to that of a Service Provider.
   *
   * When consent has not been given, the tracker may still produce events, but
   * will include a signal to the third-party service that the data use is restricted.
   * In cases where no such signal is available, the tracker should prevent firing any
   * events until consent is given.
   *
   * @see restrictsEventsWithoutConsent
   * @see https://www.bclplaw.com/images/content/1/6/v7/166081/Handbook-of-FAQs-on-Service-Providers-CCPA.pdf
   * @returns {boolean} `true` if this Tracker requires tracking consent for full capabilities; otherwise, `false`;
   */
  get requiresTrackingConsent() {
    return false;
  }

  /**
   * Returns a value indicating whether this tracker restricts firing _all_ events before
   * tracking consent is obtained. (rather than firing in a limited/restricted data processing mode).
   *
   * By default, this always returns `false`. Inheriting classes that require this type of behavior
   * should override this getter and return `true`;
   * @returns {boolean}
   */
  get restrictsEventsWithoutConsent() {
    return false;
  }

  set page(val) {
    this._page = val;
    this.pageDidSet(val);
  }

  /**
   * Enables or disables Limited/Restricted Data Usage (LDU) for this Tracker.
   * @param {boolean} val - `true` to enable LDU or `false` to disable it.
   */
  set isRestrictedDataProcessingEnabled(val) {
    this._isRestrictedDataProcessingEnabled = val;
  }

  /**
   * Gets the current status of limited/restricted data usage mode.
   * @returns {boolean}
   */
  get isRestrictedDataProcessingEnabled() {
    return this._isRestrictedDataProcessingEnabled;
  }

  logDebug(message, params = {}, ...rest) {
    if (typeof params === 'string') {
      rest.reverse().push(params);
      rest.reverse();
      params = {};
    }
    return this.log(message, {__level: 'debug', ...params}, ...rest);
  }

  logError(message, params = {}, ...rest) {
    if (typeof params === 'string') {
      rest.reverse().push(params);
      rest.reverse();
      params = {};
    }
    return this.log(message, {__level: 'error', ...params}, ...rest);
  }

  logWarn(message, params = {}, ...rest) {
    if (typeof params === 'string') {
      rest.reverse().push(params);
      rest.reverse();
      params = {};
    }
    return this.log(message, {__level: 'warn', ...params}, ...rest);
  }

  log(message, {__level, ...params} = {}, ...rest) {
    if (typeof message === 'object') {
      params = {...params, ...message};
      message = '';
    }

    let logMethod;
    switch (__level) {
      case 'debug':
        if (!this.debug) {
          return;
        }
        logMethod = window.console && window.console.debug;
        break;
      case 'warn':
      case 'warning':
        logMethod = window.console && window.console.warn;
        break;
      case 'error':
        logMethod = window.console && window.console.error;
        break;
      default:
        logMethod = window.console && window.console.log;
        break;
    }

    const cls = `${(`${this.getName()}                                  `).slice(0, 20)}:`;
    if (logMethod) {
      if (typeof logMethod.bind === 'function') {
        logMethod = logMethod.bind(window.console);
      }
      let logParams = [];
      if (Object.keys(params).length > 0) {
        logParams.push(params);
      }
      logParams = [...logParams, ...rest];
      logMethod(cls, message, ...logParams);
    }
  }

  disable() { this.enabled = false; }

  enable() { this.enabled = true; }
}
